'use es6';

import { useCallback, useMemo } from 'react';
import styled from 'foundations-theming/utils';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import PropTypes from 'prop-types';
import { contains } from 'react-immutable-proptypes';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { FORECAST_MEASUREMENTS } from '../DealForecastConstants';
import { DealStageName, provideDealStage } from './DealStageName';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CustomHighchartsTooltip, TooltipHeader, TooltipBody, TooltipBodyListContainer, TooltipBodyLabel, TooltipBodyValue, TooltipSeriesSymbol } from 'reporting-visualizations/highcharts/options/customTooltipStyles';
import { useTheme } from 'foundations-theming-specialty/utils';
import { stepGradientColorsArray } from 'reporting-visualizations/color/interpolate';
import { tokenizedFunnelColorsSetting } from 'reporting-visualizations/color/themes';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const stageType = contains({
  stage: PropTypes.string.isRequired,
  percent: PropTypes.number.isRequired,
  predictedAmount: PropTypes.number.isRequired,
  'dealstage.probability': PropTypes.number.isRequired
});
export const ForecastStage = provideDealStage(({
  stage,
  onDrilldown,
  showLabels,
  currencyCode,
  dealStage,
  isMobile,
  first,
  last
}) => {
  const trellisTheme = useTheme();
  const funnelColors = useMemo(() => {
    return stepGradientColorsArray(10, [{
      positionFraction: 0,
      value: trellisTheme.color[tokenizedFunnelColorsSetting.startColor]
    }, {
      positionFraction: 1,
      value: trellisTheme.color[tokenizedFunnelColorsSetting.endColor]
    }]);
  }, [trellisTheme]);
  const getColor = useCallback(percent => {
    let index = Math.floor(10 * percent);
    if (index === 10) {
      index -= 1;
    }
    return funnelColors[index];
  }, [funnelColors]);
  const dealStageProbability = color => {
    return /*#__PURE__*/_jsx(Text, {
      as: "span",
      color: color,
      marginInlineStart: 100,
      wordBreak: "normal",
      children: /*#__PURE__*/_jsx(FormattedPercentage, {
        value: stage.get('dealstage.probability') * 100
      })
    });
  };
  const barHeight = isMobile ? parseInt(FORECAST_MEASUREMENTS.BAR_HEIGHT, 10) / 2 : parseInt(FORECAST_MEASUREMENTS.BAR_HEIGHT, 10);
  const StyledBar = styled.div.withConfig({
    displayName: "ForecastStage__StyledBar",
    componentId: "sc-10q787r-0"
  })(["border-start-start-radius:", ";border-end-start-radius:", ";border-start-end-radius:", ";border-end-end-radius:", ";height:", "px;min-width:", ";margin-bottom:", ";width:100%;&:hover{opacity:0.7;}"], ({
    theme
  }) => first ? theme.borderRadius['200'] : 0, ({
    theme
  }) => first ? theme.borderRadius['200'] : 0, ({
    theme
  }) => last ? theme.borderRadius['200'] : 0, ({
    theme
  }) => last ? theme.borderRadius['200'] : 0, barHeight, ({
    theme
  }) => theme.space['100'], ({
    theme
  }) => theme.space['200']);
  const dealStageName = /*#__PURE__*/_jsx(DealStageName, {
    stageId: stage.get('stage')
  });
  const dealStageNameWithPipeline = /*#__PURE__*/_jsx(DealStageName, {
    stageId: stage.get('stage'),
    showPipelineLabel: true
  });
  const abbreviatedAmount = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
    value: stage.get('predictedAmount'),
    abbreviate: true,
    maximumFractionDigits: 2,
    currencyCode: currencyCode
  });
  const fullAmount = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
    value: stage.get('predictedAmount'),
    precision: 0,
    currencyCode: currencyCode
  });
  const amountLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: 'reporting-ui-components.customWidgets.forecastWidget.amountLabel'
  });
  let drilldown = null;
  if (!isMobile) {
    drilldown = dealStage && dealStage.referencedObject ? () => onDrilldown(dealStage.referencedObject.get('label')) : () => {
      onDrilldown('');
    };
  }
  const tooltipContent = () => /*#__PURE__*/_jsxs(CustomHighchartsTooltip, {
    children: [/*#__PURE__*/_jsx(TooltipHeader, {
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [dealStageNameWithPipeline, " -", dealStageProbability('fill-surface-default')]
      })
    }), /*#__PURE__*/_jsx(TooltipBody, {
      children: /*#__PURE__*/_jsxs(TooltipBodyListContainer, {
        children: [/*#__PURE__*/_jsx(TooltipSeriesSymbol, {
          color: getColor(stage.get('dealstage.probability'))
        }), /*#__PURE__*/_jsx(TooltipBodyLabel, {
          children: amountLabel
        }), /*#__PURE__*/_jsx(TooltipBodyValue, {
          children: fullAmount
        })]
      })
    })]
  });
  const bar = /*#__PURE__*/_jsx(Tooltip, {
    padding: "0",
    Content: tooltipContent,
    use: "longform",
    children: /*#__PURE__*/_jsx(StyledBar, {
      use: "unstyled",
      "data-test-id": `${stage.get('stage')}-forecast-bar`,
      onClick: drilldown,
      style: {
        backgroundColor: getColor(stage.get('dealstage.probability'))
      }
    })
  });
  if (!showLabels) {
    return /*#__PURE__*/_jsx(View, {
      paddingBlockStart: 900,
      paddingBlockEnd: 600,
      children: bar
    });
  } else {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        marginInline: 100,
        marginBlockEnd: 200,
        color: "fill-accent-neutral-default",
        typography: "body-400",
        children: abbreviatedAmount
      }), bar, /*#__PURE__*/_jsxs(View, {
        "data-test-id": "forecast-stage-label",
        blockSize: "24px",
        children: [/*#__PURE__*/_jsx(Link, {
          use: 'mention',
          onClick: drilldown,
          children: /*#__PURE__*/_jsx(Text, {
            as: "span",
            textTransform: "uppercase",
            typography: "body-125",
            color: "fill-accent-neutral-default",
            wordBreak: "break-all",
            children: dealStageName
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "span",
          children: dealStageProbability('icon-core-subtle')
        })]
      })]
    });
  }
});
ForecastStage.propTypes = {
  stage: stageType.isRequired,
  currencyCode: PropTypes.string.isRequired,
  showLabels: PropTypes.bool.isRequired,
  onDrilldown: PropTypes.func.isRequired
};